import React, { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    dashboard: 'Dashboard',
    assets: 'Assets',
    workOrders: 'Work Orders',
    reports: 'Reports',
    faultReport: 'Fault Report',
    bozp: 'BOZP',
    incidents: 'Incidents',
    zones: 'Zones',
    logout: 'Logout',
    
    // Login
    login: 'Login',
    loginTitle: 'Facility Management System',
    loginSubtitle: 'Digital facility management with cost tracking and CAPEX analysis',
    selectRole: 'Select Role',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    signIn: 'Sign In',
    loginHint: 'Use any email and password to sign in.',
    
    // Roles
    facilityManager: 'Facility Manager',
    maintenanceTechnician: 'Maintenance Technician',
    safetyOfficer: 'Safety Officer',
    management: 'Management',
    
    // Dashboard
    welcome: 'Welcome',
    overview: 'Overview',
    totalAssets: 'Total Assets',
    openWorkOrders: 'Open Work Orders',
    monthlyOpex: 'Monthly OPEX',
    avgRepairTime: 'Avg Repair Time',
    recentFaults: 'Recent Faults',
    costAnalysis: 'Cost Analysis',
    assetHealth: 'Asset Health',
    upcomingMaintenance: 'Upcoming Maintenance',
    
    // Assets
    assetRegistry: 'Asset Registry',
    assetDetails: 'Asset Details',
    addAsset: 'Add Asset',
    area: 'Area',
    building: 'Building',
    equipment: 'Equipment',
    status: 'Status',
    lastMaintenance: 'Last Maintenance',
    nextMaintenance: 'Next Maintenance',
    totalCost: 'Total Cost',
    installDate: 'Install Date',
    warrantyExpiry: 'Warranty Expiry',
    
    // Work Orders
    workOrderList: 'Work Order List',
    createWorkOrder: 'Create Work Order',
    workOrderDetails: 'Work Order Details',
    priority: 'Priority',
    assignedTo: 'Assigned To',
    dueDate: 'Due Date',
    opex: 'OPEX',
    capex: 'CAPEX',
    description: 'Description',
    attachments: 'Attachments',
    history: 'History',
    
    // Statuses
    operational: 'Operational',
    maintenance: 'Maintenance',
    critical: 'Critical',
    offline: 'Offline',
    open: 'Open',
    inProgress: 'In Progress',
    completed: 'Completed',
    high: 'High',
    medium: 'Medium',
    low: 'Low',
    
    // Fault Report
    reportFault: 'Report Fault',
    selectAsset: 'Select Asset',
    faultDescription: 'Fault Description',
    uploadPhoto: 'Upload Photo',
    submit: 'Submit',
    cancel: 'Cancel',
    
    // Repair vs Replace
    repairVsReplace: 'Repair vs Replace Analysis',
    costComparison: 'Cost Comparison',
    recommendation: 'Recommendation',
    repairCosts: 'Repair Costs',
    replacementCost: 'Replacement Cost',
    historicalCosts: 'Historical Costs',
    projectedSavings: 'Projected Savings',
    
    // BOZP
    safetyIncidents: 'Safety Incidents',
    reportIncident: 'Report Incident',
    hazardousZones: 'Hazardous Zones',
    incidentType: 'Incident Type',
    severity: 'Severity',
    location: 'Location',
    dateTime: 'Date & Time',
    
    // Reports
    financialForecast: 'Financial Forecast',
    executiveSummary: 'Executive Summary',
    exportReport: 'Export Report',
    costTrends: 'Cost Trends',
    assetPerformance: 'Asset Performance',
    maintenanceSchedule: 'Maintenance Schedule',
    
    // Actions
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    view: 'View',
    filter: 'Filter',
    search: 'Search',
    export: 'Export',
    refresh: 'Refresh',
    
    // Messages
    success: 'Success',
    error: 'Error',
    loading: 'Loading',
    noData: 'No data available',
    confirmDelete: 'Are you sure you want to delete this item?',

    // 404
    pageNotFound: 'Page not found',
    pageNotFoundDescription: 'The requested address does not exist or has been moved. Return to the overview.',

    // Dashboard (extended)
    reportForManagement: 'Report for management',
    needsAttention: 'Needs attention',
    needsAttentionSub: 'Critical assets and overdue work orders',
    upcomingMaintenanceSub: 'Maintenance within 30 days, work orders within 14 days',
    noItems: 'No items.',
    overdue: 'Overdue',
    statsSubtitleBuildings: 'Across 5 buildings',
    statsSubtitleAttention: 'Requires attention',
    statsSubtitleVsMonth: 'vs last month',
    statsSubtitleImprovement: 'Improvement',

    // Work orders (actions / toasts)
    startWorkOrder: 'Start',
    completeWorkOrder: 'Complete',
    toastWoStarted: 'Work order started.',
    toastWoCompleted: 'Work order completed.',
    toastWoCreated: 'Work order created.',
    toastWoValidationError: 'Please fill in asset and work order title.',

    // Management report
    downloadPdf: 'Download PDF',
    print: 'Print',
    close: 'Close',
    replaceRecommendationsTitle: 'What is not worth repairing (replacement recommendations)',
    investmentProposalTitle: 'Investment proposal',
    bozpOverviewTitle: 'BOZP incidents overview',
    noReplacements: 'No assets currently recommended for replacement.',
    noInvestments: 'No investments to display.',
    bozpTotal: 'Total incidents',
    bozpLow: 'Low severity',
    bozpMedium: 'Medium severity',
    bozpHigh: 'High severity',

    // BOZP page
    bozpManagement: 'BOZP Management',

    // Repair vs Replace
    individualAssetAnalysis: 'Individual Asset Analysis',

    // Fault report (extended)
    reportFaultSubtitle: 'Report equipment faults with photo documentation',
    reportFaultCardDescription: 'Fill in the details below to report a fault. All fields are required.',
    describeFaultPlaceholder: 'Describe the fault in detail...',
    takeOrUploadPhoto: 'Take or upload a photo',
    photoHint: 'Click to access camera or browse files',
    removePhoto: 'Remove photo',
    faultReportSuccess: 'Fault reported successfully!',
    faultReportSuccessDescription: 'A work order has been created.',

    // Assets (detail modal)
    workOrdersSection: 'Work orders',
    reportedFaultsSection: 'Reported faults',
    moreItems: '+ {count} more',
    noOrdersOrFaults: 'No work orders or faults recorded for this asset yet.',

    // Footer
    footerCopyright: '© 2026 Rascasone Facility Management',

    // Audit
    createdBy: 'Created by',

    // Predictive Maintenance
    predictiveMaintenance: 'Predictive Maintenance',
    predictiveSubtitle: 'AI-driven transition from preventive to predictive and prescriptive maintenance',
    predictedFailures7d: 'Predicted Failures (7d)',
    avgPredictionAccuracy: 'Avg Prediction Accuracy',
    savingsVsPreventive: 'Savings vs Preventive',
    unplannedDowntime: 'Unplanned Downtime',
    sensorDashboard: 'Sensor Dashboard',
    aiRecommendations: 'AI Recommendations',
    remainingLife: 'Remaining Useful Life',
    comparisonView: 'Comparison',
    assetHealthTimeline: 'Health Timeline',
    vibration: 'Vibration',
    temperatureSensor: 'Temperature',
    acousticEmission: 'Acoustic Emission',
    pressureSensor: 'Pressure',
    daysRemaining: 'days remaining',
    failureProbability: 'Failure Probability',
    estimatedSavings: 'Estimated Savings',
    recommendedActions: 'Recommended Actions',
    annualCost: 'Annual Cost',
    preventiveLabel: 'Preventive',
    predictiveLabel: 'Predictive',
    prescriptiveLabel: 'Prescriptive',
    selectAssetToView: 'Select an asset to view sensor data',
  },
  cs: {
    // Navigation
    dashboard: 'Dashboard',
    assets: 'Aktiva',
    workOrders: 'Pracovní příkazy',
    reports: 'Reporty',
    faultReport: 'Hlášení závad',
    bozp: 'BOZP',
    incidents: 'Incidenty',
    zones: 'Zóny',
    logout: 'Odhlásit',
    
    // Login
    login: 'Přihlášení',
    loginTitle: 'Facility Management Systém',
    loginSubtitle: 'Digitální facility management se sledováním nákladů a CAPEX analýzou',
    selectRole: 'Vyberte roli',
    username: 'Uživatelské jméno',
    email: 'E-mail',
    password: 'Heslo',
    signIn: 'Přihlásit se',
    loginHint: 'Pro přihlášení využijte libovolný e-mail a heslo.',
    
    // Roles
    facilityManager: 'Facility Manager',
    maintenanceTechnician: 'Technik údržby',
    safetyOfficer: 'Bezpečnostní technik',
    management: 'Management',
    
    // Dashboard
    welcome: 'Vítejte',
    overview: 'Přehled',
    totalAssets: 'Celkem aktiv',
    openWorkOrders: 'Otevřené příkazy',
    monthlyOpex: 'Měsíční OPEX',
    avgRepairTime: 'Prům. doba opravy',
    recentFaults: 'Nedávné závady',
    costAnalysis: 'Analýza nákladů',
    assetHealth: 'Zdraví aktiv',
    upcomingMaintenance: 'Nadcházející údržba',
    
    // Assets
    assetRegistry: 'Evidence aktiv',
    assetDetails: 'Detail aktiva',
    addAsset: 'Přidat aktivum',
    area: 'Areál',
    building: 'Budova',
    equipment: 'Zařízení',
    status: 'Stav',
    lastMaintenance: 'Poslední údržba',
    nextMaintenance: 'Další údržba',
    totalCost: 'Celkové náklady',
    installDate: 'Datum instalace',
    warrantyExpiry: 'Konec záruky',
    
    // Work Orders
    workOrderList: 'Seznam pracovních příkazů',
    createWorkOrder: 'Vytvořit příkaz',
    workOrderDetails: 'Detail příkazu',
    priority: 'Priorita',
    assignedTo: 'Přiřazeno',
    dueDate: 'Termín',
    opex: 'OPEX',
    capex: 'CAPEX',
    description: 'Popis',
    attachments: 'Přílohy',
    history: 'Historie',
    
    // Statuses
    operational: 'Provozní',
    maintenance: 'Údržba',
    critical: 'Kritický',
    offline: 'Offline',
    open: 'Otevřený',
    inProgress: 'Probíhá',
    completed: 'Dokončený',
    high: 'Vysoká',
    medium: 'Střední',
    low: 'Nízká',
    
    // Fault Report
    reportFault: 'Nahlásit závadu',
    selectAsset: 'Vyberte aktivum',
    faultDescription: 'Popis závady',
    uploadPhoto: 'Nahrát fotku',
    submit: 'Odeslat',
    cancel: 'Zrušit',
    
    // Repair vs Replace
    repairVsReplace: 'Analýza Oprava vs Výměna',
    costComparison: 'Porovnání nákladů',
    recommendation: 'Doporučení',
    repairCosts: 'Náklady na opravu',
    replacementCost: 'Náklady na výměnu',
    historicalCosts: 'Historické náklady',
    projectedSavings: 'Předpokládané úspory',
    
    // BOZP
    safetyIncidents: 'Bezpečnostní incidenty',
    reportIncident: 'Nahlásit incident',
    hazardousZones: 'Nebezpečné zóny',
    incidentType: 'Typ incidentu',
    severity: 'Závažnost',
    location: 'Umístění',
    dateTime: 'Datum a čas',
    
    // Reports
    financialForecast: 'Finanční predikce',
    executiveSummary: 'Manažerské shrnutí',
    exportReport: 'Exportovat report',
    costTrends: 'Vývoj nákladů',
    assetPerformance: 'Výkon aktiv',
    maintenanceSchedule: 'Plán údržby',
    
    // Actions
    save: 'Uložit',
    edit: 'Upravit',
    delete: 'Smazat',
    view: 'Zobrazit',
    filter: 'Filtrovat',
    search: 'Hledat',
    export: 'Exportovat',
    refresh: 'Obnovit',
    
    // Messages
    success: 'Úspěch',
    error: 'Chyba',
    loading: 'Načítání',
    noData: 'Žádná data k dispozici',
    confirmDelete: 'Opravdu chcete smazat tuto položku?',

    // 404
    pageNotFound: 'Stránka nenalezena',
    pageNotFoundDescription: 'Požadovaná adresa neexistuje nebo byla přesunuta. Vraťte se na přehled.',

    // Dashboard (extended)
    reportForManagement: 'Report pro vedení',
    needsAttention: 'Vyžaduje pozornost',
    needsAttentionSub: 'Kritická zařízení a zakázky po splatnosti',
    upcomingMaintenanceSub: 'Údržba do 30 dní, zakázky do 14 dní',
    noItems: 'Žádné položky.',
    overdue: 'Po splatnosti',
    statsSubtitleBuildings: 'Napříč 5 budovami',
    statsSubtitleAttention: 'Vyžaduje pozornost',
    statsSubtitleVsMonth: 'oproti minulému měsíci',
    statsSubtitleImprovement: 'Zlepšení',

    // Work orders (actions / toasts)
    startWorkOrder: 'Zahájit',
    completeWorkOrder: 'Dokončit',
    toastWoStarted: 'Zakázka zahájena.',
    toastWoCompleted: 'Zakázka dokončena.',
    toastWoCreated: 'Zakázka byla vytvořena.',
    toastWoValidationError: 'Vyplňte asset a název zakázky.',

    // Management report
    downloadPdf: 'Stáhnout PDF',
    print: 'Tisk',
    close: 'Zavřít',
    replaceRecommendationsTitle: 'Co se nevyplatí opravovat (doporučení k výměně)',
    investmentProposalTitle: 'Návrh investic',
    bozpOverviewTitle: 'Přehled incidentů BOZP',
    noReplacements: 'Žádná zařízení momentálně nedoporučujeme k výměně.',
    noInvestments: 'Žádné investice k zobrazení.',
    bozpTotal: 'Celkem incidentů',
    bozpLow: 'Nízká závažnost',
    bozpMedium: 'Střední závažnost',
    bozpHigh: 'Vysoká závažnost',

    // BOZP page
    bozpManagement: 'BOZP Management',

    // Repair vs Replace
    individualAssetAnalysis: 'Analýza jednotlivých aktiv',

    // Fault report (extended)
    reportFaultSubtitle: 'Hlášení závad zařízení s fotodokumentací',
    reportFaultCardDescription: 'Vyplňte údaje pro nahlášení závady. Všechna pole jsou povinná.',
    describeFaultPlaceholder: 'Popište závadu podrobně...',
    takeOrUploadPhoto: 'Vyfotit nebo nahrát fotku',
    photoHint: 'Klikněte pro kameru nebo procházení souborů',
    removePhoto: 'Odstranit fotku',
    faultReportSuccess: 'Závada byla nahlášena!',
    faultReportSuccessDescription: 'Byl vytvořen pracovní příkaz.',

    // Assets (detail modal)
    workOrdersSection: 'Zakázky',
    reportedFaultsSection: 'Hlášené závady',
    moreItems: '+ {count} dalších',
    noOrdersOrFaults: 'K tomuto zařízení zatím nejsou evidovány zakázky ani závady.',

    // Footer
    footerCopyright: '© 2026 Rascasone Facility Management',

    // Audit
    createdBy: 'Vytvořil',

    // Predictive Maintenance
    predictiveMaintenance: 'Prediktivní údržba',
    predictiveSubtitle: 'AI řízený přechod od preventivní k prediktivní a preskriptivní údržbě',
    predictedFailures7d: 'Predikované poruchy (7d)',
    avgPredictionAccuracy: 'Přesnost predikce',
    savingsVsPreventive: 'Úspora vs preventivní',
    unplannedDowntime: 'Neplánované výpadky',
    sensorDashboard: 'Senzorový přehled',
    aiRecommendations: 'AI doporučení',
    remainingLife: 'Zbytková životnost',
    comparisonView: 'Srovnání',
    assetHealthTimeline: 'Časová osa zdraví',
    vibration: 'Vibrace',
    temperatureSensor: 'Teplota',
    acousticEmission: 'Akustická emise',
    pressureSensor: 'Tlak',
    daysRemaining: 'dní zbývá',
    failureProbability: 'Pravděpodobnost poruchy',
    estimatedSavings: 'Odhadovaná úspora',
    recommendedActions: 'Doporučené akce',
    annualCost: 'Roční náklady',
    preventiveLabel: 'Preventivní',
    predictiveLabel: 'Prediktivní',
    prescriptiveLabel: 'Preskriptivní',
    selectAssetToView: 'Vyberte stroj pro zobrazení senzorových dat',
  }
};

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    return translations[language][key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'cs' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
};
